import "@/App.css";
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { MnemonicProvider } from "@/contexts/MnemonicContext";
import MnemonicDevOverlay from "@/components/mnemonics/MnemonicDevOverlay";
import { ORDER_EDITOR_ROLES, USER_MANAGER_ROLES, SALES_DOCUMENT_ROLES, mustChangePassword } from "@/lib/constants";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import CreateOrderPage from "@/pages/CreateOrderPage";
import EditOrderPage from "@/pages/EditOrderPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerProfilePage from "@/pages/CustomerProfilePage";
import UsersPage from "@/pages/UsersPage";
import DispatchPage from "@/pages/DispatchPage";
import InventoryPage from "@/pages/InventoryPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import NetworkAccessPage from "@/pages/NetworkAccessPage";
import JobCardPage from "@/pages/JobCardPage";
import JobCardFillPage from "@/pages/JobCardFillPage";
import JobCardsPage from "@/pages/JobCardsPage";
import MachinesPage from "@/pages/MachinesPage";
import PlateTrackingPage from "@/pages/PlateTrackingPage";
import DieTrackingPage from "@/pages/DieTrackingPage";
import PlateRegisterPage from "@/pages/PlateRegisterPage";
import DieRegisterPage from "@/pages/DieRegisterPage";
import RecycleBinPage from "@/pages/RecycleBinPage";
import ProductionStagesPage from "@/pages/ProductionStagesPage";
import AttachmentCategoriesPage from "@/pages/AttachmentCategoriesPage";
import ReportsPage from "@/pages/ReportsPage";
import QuotationsPage from "@/pages/QuotationsPage";
import QuotationEditorPage from "@/pages/QuotationEditorPage";
import ProformaInvoicesPage from "@/pages/ProformaInvoicesPage";
import ProformaInvoiceEditorPage from "@/pages/ProformaInvoiceEditorPage";

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangePassword(user)) return <Navigate to="/change-password" replace />;
  
  // Support multi-role: check if ANY of user's roles is in allowedRoles
  if (allowedRoles) {
    const userRoles = user.roles || [user.role];
    const hasPermission = userRoles.some(r => allowedRoles.includes(r));
    if (!hasPermission) {
      return <Navigate to="/" replace />;
    }
  }
  return children;
}

// Role-specific default landing pages
// Every role needs an entry. Without one the fallback is '/', which redirects
// to Orders anyway, but a role whose work lives elsewhere would land a page
// away from it on every login.
const ROLE_LANDING_PAGES = {
  admin: '/orders',
  receptionist: '/orders',
  designer: '/job-cards',
  accountant: '/reports',
  store_keeper: '/inventory',
  dispatcher: '/dispatch',
  city_office_manager: '/orders',
  supervisor: '/job-cards',
  // Orders is the one page any authenticated role can load, and it is all the
  // shop floor and the drivers have on the web since their queues moved off it.
  machine: '/orders',
  machine_man: '/orders',  // legacy accounts, never migrated
  binder: '/orders',
  driver: '/orders',
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const landingPage = user ? (ROLE_LANDING_PAGES[user.role] || '/') : '/login';

  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={landingPage} replace />} />
      <Route path="/change-password" element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/" element={<Navigate to="/orders" replace />} />
      <Route path="/orders" element={
        <ProtectedRoute><Layout><OrdersPage /></Layout></ProtectedRoute>
      } />
      <Route path="/orders/new" element={
        <ProtectedRoute><Layout><CreateOrderPage /></Layout></ProtectedRoute>
      } />
      <Route path="/orders/:id" element={
        <ProtectedRoute><Layout><OrderDetailPage /></Layout></ProtectedRoute>
      } />
      <Route path="/orders/:id/edit" element={
        <ProtectedRoute allowedRoles={ORDER_EDITOR_ROLES}>
          <Layout><EditOrderPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/job-card/:jobCardId" element={
        <ProtectedRoute><Layout><JobCardPage /></Layout></ProtectedRoute>
      } />
      <Route path="/job-card/:jobCardId/fill" element={
        <ProtectedRoute allowedRoles={["admin", "supervisor", "designer", "receptionist"]}>
          <Layout><JobCardFillPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute><Layout><CustomersPage /></Layout></ProtectedRoute>
      } />
      <Route path="/customers/:customerId" element={
        <ProtectedRoute><Layout><CustomerProfilePage /></Layout></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={USER_MANAGER_ROLES}>
          <Layout><UsersPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute allowedRoles={["admin", "store_keeper", "receptionist"]}>
          <Layout><InventoryPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/dispatch" element={
        <ProtectedRoute allowedRoles={["admin", "dispatcher", "receptionist", "accountant", "city_office_manager"]}>
          <Layout><DispatchPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={["admin", "accountant", "city_office_manager", "dispatcher", "supervisor"]}>
          <Layout><ReportsPage /></Layout>
        </ProtectedRoute>
      } />
      {/* Quotations and proforma invoices are money documents, so they stop at
          the roles that already see order money */}
      <Route path="/quotations" element={
        <ProtectedRoute allowedRoles={SALES_DOCUMENT_ROLES}>
          <Layout><QuotationsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/quotations/new" element={
        <ProtectedRoute allowedRoles={SALES_DOCUMENT_ROLES}>
          <Layout><QuotationEditorPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/quotations/:id" element={
        <ProtectedRoute allowedRoles={SALES_DOCUMENT_ROLES}>
          <Layout><QuotationEditorPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/proforma-invoices" element={
        <ProtectedRoute allowedRoles={SALES_DOCUMENT_ROLES}>
          <Layout><ProformaInvoicesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/proforma-invoices/new" element={
        <ProtectedRoute allowedRoles={SALES_DOCUMENT_ROLES}>
          <Layout><ProformaInvoiceEditorPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/proforma-invoices/:id" element={
        <ProtectedRoute allowedRoles={SALES_DOCUMENT_ROLES}>
          <Layout><ProformaInvoiceEditorPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin-settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AdminSettingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/network-access" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><NetworkAccessPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/machines" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><MachinesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/job-cards" element={
        <ProtectedRoute allowedRoles={["admin", "supervisor", "designer", "receptionist"]}>
          <Layout><JobCardsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/plates" element={
        <ProtectedRoute allowedRoles={["admin", "designer", "supervisor", "receptionist"]}>
          <Layout><PlateTrackingPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/dies" element={
        <ProtectedRoute allowedRoles={["admin", "designer", "supervisor", "receptionist"]}>
          <Layout><DieTrackingPage /></Layout>
        </ProtectedRoute>
      } />
      {/* The store keeps the racks, so it reaches the registers the board does not admit it to */}
      <Route path="/plate-register" element={
        <ProtectedRoute allowedRoles={["admin", "designer", "supervisor", "receptionist", "store_keeper"]}>
          <Layout><PlateRegisterPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/die-register" element={
        <ProtectedRoute allowedRoles={["admin", "designer", "supervisor", "receptionist", "store_keeper"]}>
          <Layout><DieRegisterPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/recycle-bin" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><RecycleBinPage /></Layout>
        </ProtectedRoute>
      } />
      {/* Admin configuration routes */}
      <Route path="/admin-settings/stages" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><ProductionStagesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin-settings/attachment-categories" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <Layout><AttachmentCategoriesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to={landingPage} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <MnemonicProvider>
            <AppRoutes />
            <MnemonicDevOverlay />
          </MnemonicProvider>
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
